import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {useNavigate} from "react-router-dom";
import {useUserContext} from "../../../context/userContext";
import {callGetManager} from "../functions/getManager";
import ModuleLoading from "../../../modules/loading/moduleLoading";

const AdminSelectManager: FunctionComponent = () => {
  const {userData} = useUserContext();
  const navigate = useNavigate();

  const [managerNames, setManagerNames] = useState<Record<string, string>>({});
  const [namesLoading, setNamesLoading] = useState<boolean>(false);
  const [namesLoaded, setNamesLoaded] = useState<boolean>(false);

  const goToManager = useCallback(
    (managerId: string) => {
      if (!managerId) return;
      navigate(`/admin/${managerId}`);
    },
    [navigate],
  );

  const managerIds = useMemo(() => {
    if (!userData || !userData.managers) return [];
    return Object.keys(userData.managers);
  }, [userData]);

  useEffect(() => {
    if (!managerIds.length) return;

    let cancelled = false;

    const loadNames = async () => {
      setNamesLoading(true);
      setNamesLoaded(false);

      try {
        const results = await Promise.all(
          managerIds.map(async (managerId) => {
            try {
              const res: any = await (callGetManager as any)(managerId);
              const name =
                res?.manager?.name ??
                res?.data?.manager?.name ??
                res?.data?.name ??
                res?.name;

              return [managerId, name || managerId] as const;
            } catch {
              return [managerId, managerId] as const;
            }
          }),
        );

        if (cancelled) return;

        const namesObject = results.reduce<Record<string, string>>(
          (acc, [id, name]) => {
            acc[id] = name;
            return acc;
          },
          {},
        );

        setManagerNames(namesObject);
        setNamesLoaded(true);
      } finally {
        if (!cancelled) setNamesLoading(false);
      }
    };

    void loadNames();

    return () => {
      cancelled = true;
    };
  }, [managerIds]);

  const isLoading = useMemo(() => {
    if (userData === undefined) return true;
    if (userData === null) return false;
    if (!userData.managers) return false;
    if (!managerIds.length) return false;
    return namesLoading || !namesLoaded;
  }, [userData, managerIds.length, namesLoading, namesLoaded]);

  if (userData === null || (userData !== undefined && !userData?.managers)) {
    return null;
  }

  if (isLoading) {
    return (
      <div
        style={{
          marginTop: 24,
          marginBottom: 34,
          display: "flex",
          justifyContent: "center",
        }}>
        <ModuleLoading/>
      </div>
    );
  }

  return (
    <div style={{maxWidth: 640, margin: "0 auto", padding: 16}}>
      <h2 style={{marginBottom: 12}}>Velg manager</h2>

      <div style={{display: "flex", flexDirection: "column", gap: 10}}>
        {managerIds.map((managerId) => (
          <button
            key={managerId}
            type="button"
            onClick={() => goToManager(managerId)}
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              border: "1px solid rgba(0,0,0,0.12)",
              background: "#fff",
              textAlign: "left",
              cursor: "pointer",
            }}>
            <div style={{fontWeight: 600}}>
              {managerNames[managerId] ?? managerId}
            </div>
            <div style={{fontSize: 12, opacity: 0.7}}></div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdminSelectManager;
